import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/organization/departments")(
  {
    component: DepartmentDataTable,
  }
);

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { DepartmentFormSheet } from "@/components/crud/forms/DepartmentFormSheet";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { useDebounce } from "@/hooks/use-debounce";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";

// Hooks
import useCreateDepartment from "@/hooks/api/organization/organization-departments/useCreateDepartment";
import useUpdateDepartment from "@/hooks/api/organization/organization-departments/useUpdateDepartment";
import useDeleteDepartment from "@/hooks/api/organization/organization-departments/useDeleteDepartment";
import useFetchDepartments, {
  type DepartmentOrdering,
} from "@/hooks/api/organization/organization-departments/useFetchDepartments";
import type {
  Department,
  DepartmentPayload,
} from "@/types/organization/department";
import { DepartmentTable } from "@/components/crud/table/DepartmentTable";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function DepartmentDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "organization.structure.manage");
  // UI State
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(
    null
  );
  const [departmentToDelete, setDepartmentToDelete] =
    useState<Department | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Ordering
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        created_at: SortDirection;
      },
      DepartmentOrdering
    >({
      name: null,
      created_at: null,
    });

  // Global Search Filter
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Department Crud Hooks
  const departmentsQuery = useFetchDepartments({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
  });
  const departments = departmentsQuery.data?.results ?? [];
  const count = departmentsQuery.data?.count ?? 0;

  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();

  const isMutating =
    createDepartment.isPending ||
    updateDepartment.isPending ||
    deleteDepartment.isPending;
  const isLoading = departmentsQuery.isPending || isMutating;
  const isFetching = departmentsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, setPage]);

  // For resetting filters
  const resetFilters = () => {
    setOrdering({
      name: null,
      created_at: null,
    });
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  // Handlers
  const handleCreate = () => {
    setEditingDepartment(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (department: Department) => {
    setEditingDepartment(department);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (department: Department) => {
    setDepartmentToDelete(department);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!departmentToDelete) return;
    try {
      await deleteDepartment.mutateAsync({ id: departmentToDelete.id });
      toast.success("Department deleted successfully!");
      setIsDeleteDialogOpen(false);
      setDepartmentToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: DepartmentPayload) => {
    if (editingDepartment) {
      await updateDepartment.mutateAsync({
        id: editingDepartment.id,
        payload: data,
      });
      toast.success("Department updated successfully!");
    } else {
      await createDepartment.mutateAsync(data);
      toast.success("Department created successfully!");
    }

    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Departments"
          description="Manage work departments here."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Department"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search departments..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 max-w-sm font-light"
            />
          </div>
        </FiltersPanel>

        {/* Departments Table */}
        <DepartmentTable
          departments={departments}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* Department Form Sheet */}
        <DepartmentFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingDepartment={editingDepartment}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Department"
          description={`Are you sure you want to delete department "${departmentToDelete?.name}"? This action cannot be undone.`}
          itemName={departmentToDelete?.name}
          isLoading={deleteDepartment.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
